import { useState, useEffect, useMemo } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Column, Modal, Input, Alert, AlertTitle, AlertDescription, DeleteConfirmDialog } from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { MobileFilterSheet } from '@/components/layout/MobileFilterSheet';
import {
  Plus,
  Upload,
  FolderOpen,
  Loader2,
  Activity,
  Download,
  Tag,
  Network,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Edit,
  FileSpreadsheet,
  CircleDot,
  Network as NetworkIcon,
  Hash,
  Calendar,
  Merge,
  Eye,
  Wifi,
  MapPin,
  Smartphone,
  Lock,
  Unlock as LockOpen,
  AlertTriangle,
} from 'lucide-react';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { collection, query, orderBy, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  getProfilesForOrg,
  getSharedProfiles,
  getGroupsForOrg,
  loadNetworkCatalog,
  createProfile,
  updateProfile,
  deleteProfile,
  createGroup,
  updateGroup,
  deleteGroup,
  mergeGroup,
  getInventoryStats,
  parseQRCodeFromFile,
  parseCSV,
  batchImportProfiles,
  downloadCSVTemplate,
  validateLpaString,
} from '@/services/esimService';
import type {
  ESimProfile,
  ESimGroup,
  Network as NetworkType,
  DownloadLimitType,
  ESimProfileStatus,
  ESimInventoryStats,
} from '@simcheck/shared-types';
import { getESimLockInfo, type ESimLockState } from '@simcheck/shared-types';
import { getLocations, getCountries } from '@/services/hierarchicalLocationService';
import { cn, formatRelativeTime } from '@/lib/utils';
import { useToast } from '@/contexts/ToastContext';
import { NetworkCombobox } from '@/components/esim/NetworkCombobox';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import {
  GroupMovingBadge,
  GroupRelocationAlert,
  ProfileNeedsAttentionBadge,
  ProfileNeedsAttentionAlert,
} from '@/components/esim/RelocationNotices';
import {
  recentRelocationHint,
  relocationHintTargetImei,
  profileNeedsAttention,
} from '@/utils/esimRelocationNotices';

// Editable fields of the profile detail modal, staged locally until Save.
interface ProfileDraft {
  name: string;
  networkId: string;
  networkName: string;
  apn: string;
  msisdn: string;
  downloadLimit: DownloadLimitType;
  /** `''` while the numeric input is empty/invalid — validated on Save. */
  maxDownloads: number | '';
  groupId: string;
  description: string;
  tags: string[];
  /** Customer-declared lock: the SIM stays on the modem it is installed on. */
  locked: boolean;
}

const buildProfileDraft = (p: ESimProfile): ProfileDraft => ({
  name: p.name || '',
  networkId: p.networkId || '',
  networkName: p.networkName || '',
  apn: p.apn || '',
  msisdn: p.msisdn || '',
  downloadLimit: p.downloadLimit || 'unlimited',
  // Sensible cap if the user switches to "limited": at least one more than
  // already used, so the profile can't be saved straight into "exhausted".
  maxDownloads: p.maxDownloads ?? Math.max(1, (p.downloadCount || 0) + 1),
  groupId: p.groupId || '',
  description: p.description || '',
  tags: [...(p.tags || [])],
  locked: p.locked === true,
});

// Filter column configuration helper for bazza/ui DataTableFilter
const filterColumnHelper = createColumnConfigHelper<ESimProfile>();

// Static filter configs (status has fixed options)
const getStaticFilterConfigs = () => [
  filterColumnHelper
    .text()
    .id('name')
    .accessor((row) => row.name)
    .displayName('Name')
    .icon(Package)
    .build(),
  filterColumnHelper
    .option()
    .id('status')
    .accessor((row) => row.status)
    .displayName('Status')
    .icon(CircleDot)
    .options([
      { label: 'Available', value: 'available' },
      { label: 'In Use', value: 'in_use' },
      { label: 'Downloaded', value: 'downloaded' },
      { label: 'Expired', value: 'expired' },
      { label: 'Disabled', value: 'disabled' },
    ])
    .build(),
  // #375: a failed move left the profile on no modem.
  filterColumnHelper
    .option()
    .id('attention')
    .accessor((row) => (profileNeedsAttention(row.needsAttention) ? 'needs_attention' : 'ok'))
    .displayName('Attention')
    .icon(AlertTriangle)
    .options([
      { label: 'Needs attention', value: 'needs_attention' },
      { label: 'OK', value: 'ok' },
    ])
    .build(),
];

export function ESimInventory() {
  const { activeOrganization, currentUser, isSuperAdmin, isViewingAsOrg, userRole } = useAuth();
  const fmt = useDateFormat();
  // Super-admin "global" context (not impersonating a tenant, no active org).
  // Only here does the inventory list surface the shared eSIM inventory.
  const sharedMode = isSuperAdmin && !isViewingAsOrg && !activeOrganization;
  // Who can add a network the picker doesn't list, and at what scope:
  // super admins choose per network (system-wide vs this org); org owners/admins
  // add scoped to their own org; everyone else leaves it blank.
  const networkCreateScope: 'org' | 'choose' | undefined = isSuperAdmin
    ? 'choose'
    : userRole === 'owner' || userRole === 'admin'
      ? 'org'
      : undefined;
  const { success, error: showError } = useToast();

  // Data state
  const [profiles, setProfiles] = useState<ESimProfile[]>([]);
  const [groups, setGroups] = useState<ESimGroup[]>([]);
  // The full network catalogue (~2.5k) is loaded lazily by the Network
  // combobox; the page no longer eager-loads it. `newProfileNetwork` holds the
  // catalogue entry chosen in the Add-Profile combobox so we can denormalize
  // its name onto the profile.
  const [newProfileNetwork, setNewProfileNetwork] = useState<NetworkType | null>(null);
  const [stats, setStats] = useState<ESimInventoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  // Resolves the `installedOn` pointer's raw deviceId/imei into friendly labels
  // for the "Installed On" column. Keyed by both device `id` and `deviceId`
  // (the pointer stores whichever the reconcile saw); modem models keyed by IMEI.
  // We surface the device's LOCATION name (where the modem physically sits),
  // not the internal device name. Best-effort — if the device fetch fails the
  // column falls back to raw ids.
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});
  const [modemModels, setModemModels] = useState<Record<string, string>>({});
  // Location label per modem IMEI, for naming the modem in a group's
  // relocation hint (#375).
  const [modemLocations, setModemLocations] = useState<Record<string, string>>({});
  // Where each holder device physically is: country + city for the inventory
  // columns. Keyed like `locationNames` (device `id` and `deviceId`).
  const [installedPlaces, setInstalledPlaces] = useState<Record<string, { country: string; countryCode: string; city: string }>>({});

  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBatchUploadModal, setShowBatchUploadModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showProfileDetailModal, setShowProfileDetailModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<ESimProfile | null>(null);
  // Detail-modal edits accumulate in a draft and are written in ONE
  // updateProfile call on Save — nothing persists until the user commits.
  const [profileDraft, setProfileDraft] = useState<ProfileDraft | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [showDeleteProfileConfirm, setShowDeleteProfileConfirm] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);
  // Empty group waiting for confirmation in the DeleteConfirmDialog
  const [pendingGroupDelete, setPendingGroupDelete] = useState<ESimGroup | null>(null);

  // Multi-select state
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [showBulkGroupModal, setShowBulkGroupModal] = useState(false);
  const [showBulkTagsModal, setShowBulkTagsModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkGroupId, setBulkGroupId] = useState('');
  const [bulkTagsInput, setBulkTagsInput] = useState('');
  const [bulkTagMode, setBulkTagMode] = useState<'add' | 'replace'>('add');
  const [bulkOperating, setBulkOperating] = useState(false);
  const [detailTagInput, setDetailTagInput] = useState('');

  // Form state
  const [uploadMode, setUploadMode] = useState<'qr' | 'lpa'>('lpa');
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileDescription, setNewProfileDescription] = useState('');
  const [newProfileApn, setNewProfileApn] = useState('');
  const [newProfileLpaString, setNewProfileLpaString] = useState('');
  const [newProfileNetworkId, setNewProfileNetworkId] = useState('');
  const [newProfileDownloadLimit, setNewProfileDownloadLimit] = useState<DownloadLimitType>('unlimited');
  const [newProfileMaxDownloads, setNewProfileMaxDownloads] = useState<number>(5);
  // "This SIM cannot be downloaded again" — set by the customer when adding it.
  const [newProfileLocked, setNewProfileLocked] = useState(false);
  const [newProfileGroupId, setNewProfileGroupId] = useState('');
  const [newProfileTags, setNewProfileTags] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Group form state
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#3B82F6');
  const [newGroupApn, setNewGroupApn] = useState('');

  // Group manager state (issue #160): one row at a time flips into an inline
  // edit form or a merge-target picker.
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');
  const [editGroupColor, setEditGroupColor] = useState('#3B82F6');
  const [editGroupApn, setEditGroupApn] = useState('');
  const [mergingGroupId, setMergingGroupId] = useState<string | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [groupActionBusy, setGroupActionBusy] = useState(false);

  // Batch upload state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [batchImporting, setBatchImporting] = useState(false);

  // Build dynamic filter config with network/group/tags options from state
  const filterColumnsConfig = useMemo(() => {
    // Get unique tags from all profiles
    const uniqueTags = [...new Set(profiles.flatMap(p => p.tags || []))].sort();

    // Scope the Network filter to networks actually referenced by the loaded
    // profiles — the full catalogue is ~2.5k entries and would make this
    // dropdown unusable. Uses the denormalized networkName on each profile.
    const usedNetworks = [
      ...new Map(
        profiles
          .filter((p) => p.networkId)
          .map((p) => [p.networkId, { label: p.networkName || p.networkId, value: p.networkId }])
      ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label));

    return [
      ...getStaticFilterConfigs(),
      filterColumnHelper
        .option()
        .id('networkId')
        .accessor((row) => row.networkId)
        .displayName('Network')
        .icon(NetworkIcon)
        .options(usedNetworks)
        .build(),
      filterColumnHelper
        .option()
        .id('groupId')
        .accessor((row) => row.groupId || 'none')
        .displayName('Group')
        .icon(FolderOpen)
        .options([
          { label: 'No Group', value: 'none' },
          ...groups.map(g => ({ label: g.name, value: g.id }))
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('downloadLimit')
        .accessor((row) => row.downloadLimit || 'unlimited')
        .displayName('Limit Type')
        .icon(Download)
        .options([
          { label: 'Unlimited', value: 'unlimited' },
          { label: 'Single Use', value: 'single_use' },
          { label: 'Limited', value: 'limited' },
        ])
        .build(),
      // Lock state: can this SIM ever move to another modem?
      filterColumnHelper
        .option()
        .id('lockState')
        .accessor((row) => getESimLockInfo(row).state)
        .displayName('Lock')
        .icon(Lock)
        .options([
          { label: 'Locked to a modem', value: 'locked' },
          { label: 'Locks on install', value: 'locks_on_install' },
          { label: 'Re-downloadable', value: 'redownloadable' },
          { label: 'Spent', value: 'spent' },
        ])
        .build(),
      // Tags multi-select filter - matches if any tag is selected
      filterColumnHelper
        .option()
        .id('tags')
        .accessor((row) => (row.tags || []).join(','))
        .displayName('Tags')
        .icon(Tag)
        .options(uniqueTags.map(t => ({ label: t, value: t })))
        .build(),
      // Date range filter for createdAt
      filterColumnHelper
        .option()
        .id('createdAt')
        .accessor((row) => {
          if (!row.createdAt) return 'all';
          const now = new Date();
          const created = row.createdAt instanceof Date ? row.createdAt : new Date(row.createdAt);
          const diffMs = now.getTime() - created.getTime();
          const diffDays = diffMs / (1000 * 60 * 60 * 24);
          if (diffDays <= 1) return '24h';
          if (diffDays <= 7) return '7d';
          if (diffDays <= 30) return '30d';
          if (diffDays <= 90) return '90d';
          return 'older';
        })
        .displayName('Created')
        .icon(Calendar)
        .options([
          { label: 'Last 24 hours', value: '24h' },
          { label: 'Last 7 days', value: '7d' },
          { label: 'Last 30 days', value: '30d' },
          { label: 'Last 90 days', value: '90d' },
          { label: 'Older', value: 'older' },
        ])
        .build(),
    ];
  }, [groups, profiles]);

  // bazza/ui DataTableFilter hook
  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: profiles,
    columnsConfig: filterColumnsConfig,
  });

  // Apply filters to profiles data
  const filteredProfiles = useMemo(() =>
    applyFilters(profiles, filters, filterColumnsConfig),
    [profiles, filters, filterColumnsConfig]
  );

  // Groups whose eSIMs moved between modems too often today or yesterday (#375).
  const movingGroups = useMemo(
    () => groups.filter((g) => recentRelocationHint(g.relocationHint)),
    [groups]
  );
  const renderGroupRelocationAlert = (group: ESimGroup, className?: string) => {
    const hint = recentRelocationHint(group.relocationHint);
    if (!hint) return null;
    const imei = relocationHintTargetImei(hint);
    return (
      <GroupRelocationAlert
        hint={hint}
        modemModel={imei ? modemModels[imei] : undefined}
        modemLocation={imei ? modemLocations[imei] : undefined}
        className={className}
      />
    );
  };

  // Load data
  useEffect(() => {
    // Super-admin global context: no active org, but we still show the shared
    // eSIM inventory (org-scoped queries would throw on a null org).
    if (!activeOrganization && !sharedMode) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        if (sharedMode) {
          const [sharedData, devicesData] = await Promise.all([
            getSharedProfiles(),
            edgeDeviceService.getDevices().catch(() => []),
          ]);
          setProfiles(sharedData);
          setGroups([]);
          setStats(null);
          const names: Record<string, string> = {};
          const models: Record<string, string> = {};
          for (const d of devicesData) {
            const label = d.locationName || d.name || d.deviceId || d.id;
            if (d.id) names[d.id] = label;
            if (d.deviceId) names[d.deviceId] = label;
            for (const m of d.modems || []) {
              if (m.imei && m.model) models[m.imei] = m.model;
            }
          }
          setLocationNames(names);
          setModemModels(models);
          setModemLocations({});
          return;
        }

        const [profilesData, groupsData, statsData, devicesData, locationsData, countriesData] = await Promise.all([
          getProfilesForOrg(activeOrganization!.id),
          getGroupsForOrg(activeOrganization!.id),
          getInventoryStats(activeOrganization!.id),
          // Best-effort: resolve `installedOn` deviceId/imei → friendly labels.
          // Never fail the page load over it — the column degrades to raw ids.
          edgeDeviceService.getDevices().catch(() => []),
          // Country / city of the site each holder modem sits at.
          getLocations().catch(() => []),
          getCountries().catch(() => []),
        ]);
        setProfiles(profilesData);
        setGroups(groupsData);
        setStats(statsData);

        const names: Record<string, string> = {};
        const models: Record<string, string> = {};
        const modemLocs: Record<string, string> = {};
        for (const d of devicesData) {
          // Prefer the location name (where the modem sits); fall back to the
          // device name and finally the raw id so the cell is never blank.
          const label = d.locationName || d.name || d.deviceId || d.id;
          if (d.id) names[d.id] = label;
          if (d.deviceId) names[d.deviceId] = label;
          for (const m of d.modems || []) {
            if (m.imei && m.model) models[m.imei] = m.model;
            if (m.imei) modemLocs[m.imei] = label;
          }
        }
        setLocationNames(names);
        setModemModels(models);
        setModemLocations(modemLocs);

        const locById = new Map((locationsData as any[]).map((l) => [l.id, l]));
        const countryById = new Map((countriesData as any[]).map((c) => [c.id, c]));
        const places: Record<string, { country: string; countryCode: string; city: string }> = {};
        for (const d of devicesData as any[]) {
          const loc = d.locationId ? locById.get(d.locationId) : undefined;
          const country = countryById.get(loc?.countryId || d.countryId);
          const place = { country: country?.name || '', countryCode: country?.code || '', city: loc?.city || '' };
          if (d.id) places[d.id] = place;
          if (d.deviceId) places[d.deviceId] = place;
        }
        setInstalledPlaces(places);
      } catch (err: any) {
        console.error('Error loading eSIM data:', err);
        showError('Failed to load eSIM inventory');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeOrganization, sharedMode]);

  // Reset form
  const resetForm = () => {
    setNewProfileName('');
    setNewProfileDescription('');
    setNewProfileApn('');
    setNewProfileLpaString('');
    setNewProfileNetworkId('');
    setNewProfileNetwork(null);
    setNewProfileDownloadLimit('unlimited');
    setNewProfileMaxDownloads(5);
    setNewProfileLocked(false);
    setNewProfileGroupId('');
    setNewProfileTags('');
    setUploadMode('lpa');
  };

  // Handle QR code upload
  const handleQRUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const lpaString = await parseQRCodeFromFile(file);
      setNewProfileLpaString(lpaString);
      success('QR code parsed successfully');
    } catch (err: any) {
      showError(err.message || 'Failed to parse QR code');
    }
  };

  // Handle create profile
  const handleCreateProfile = async () => {
    if (!activeOrganization || !currentUser) return;

    if (!newProfileName || !newProfileLpaString) {
      showError('Please fill in all required fields');
      return;
    }

    if (!validateLpaString(newProfileLpaString)) {
      showError('Invalid LPA string format');
      return;
    }

    setFormSubmitting(true);
    try {
      const network = newProfileNetwork;
      const group = groups.find(g => g.id === newProfileGroupId);

      await createProfile(activeOrganization.id, currentUser.uid, {
        name: newProfileName,
        description: newProfileDescription || undefined,
        lpaString: newProfileLpaString,
        networkId: newProfileNetworkId,
        networkName: network?.name,
        apn: newProfileApn.trim() || undefined,
        downloadLimit: newProfileDownloadLimit,
        maxDownloads: newProfileDownloadLimit === 'limited' ? newProfileMaxDownloads : undefined,
        locked: newProfileLocked,
        groupId: newProfileGroupId || undefined,
        groupName: group?.name,
        tags: newProfileTags ? newProfileTags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });

      success('Profile created successfully');
      setShowUploadModal(false);
      resetForm();

      // Reload data
      const [profilesData, statsData] = await Promise.all([
        getProfilesForOrg(activeOrganization.id),
        getInventoryStats(activeOrganization.id),
      ]);
      setProfiles(profilesData);
      setStats(statsData);
    } catch (err: any) {
      showError(err.message || 'Failed to create profile');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle create group
  const handleCreateGroup = async () => {
    if (!activeOrganization || !currentUser) return;

    if (!newGroupName) {
      showError('Please enter a group name');
      return;
    }

    setFormSubmitting(true);
    try {
      await createGroup(activeOrganization.id, currentUser.uid, {
        name: newGroupName,
        description: newGroupDescription || undefined,
        color: newGroupColor,
        apn: newGroupApn.trim() || undefined,
      });

      success('Group created successfully');
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupColor('#3B82F6');
      setNewGroupApn('');

      // Reload groups (modal stays open so the new group appears in the list)
      const groupsData = await getGroupsForOrg(activeOrganization.id);
      setGroups(groupsData);
    } catch (err: any) {
      showError(err.message || 'Failed to create group');
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Group manager actions (issue #160) ─────────────────────────────────

  const startEditGroup = (group: ESimGroup) => {
    setMergingGroupId(null);
    setEditingGroupId(group.id);
    setEditGroupName(group.name);
    setEditGroupDescription(group.description || '');
    setEditGroupColor(group.color || '#3B82F6');
    setEditGroupApn(group.apn || '');
  };

  const handleSaveGroupEdit = async () => {
    if (!activeOrganization || !editingGroupId) return;
    if (!editGroupName.trim()) {
      showError('Group name cannot be empty');
      return;
    }

    setGroupActionBusy(true);
    try {
      await updateGroup(editingGroupId, activeOrganization.id, {
        name: editGroupName.trim(),
        description: editGroupDescription.trim() || undefined,
        color: editGroupColor,
        apn: editGroupApn, // blank clears (service normalizes to null)
      });
      success('Group updated');
      setEditingGroupId(null);
      // Rename propagates to members' denormalized groupName — reload both.
      await reloadData();
    } catch (err: any) {
      showError(err.message || 'Failed to update group');
    } finally {
      setGroupActionBusy(false);
    }
  };

  const handleMergeGroup = async () => {
    if (!activeOrganization || !mergingGroupId || !mergeTargetId) return;

    setGroupActionBusy(true);
    try {
      const moved = await mergeGroup(mergingGroupId, mergeTargetId, activeOrganization.id);
      const targetName = groups.find(g => g.id === mergeTargetId)?.name || 'target group';
      success(`Moved ${moved} profile(s) into ${targetName} and deleted the group`);
      setMergingGroupId(null);
      setMergeTargetId('');
      await reloadData();
    } catch (err: any) {
      showError(err.message || 'Failed to merge groups');
    } finally {
      setGroupActionBusy(false);
    }
  };

  // "View profiles" on a group row: close the manager and filter the table
  // to that group so membership is visible at a glance.
  const handleViewGroupProfiles = (group: ESimGroup) => {
    const groupColumn = filterColumns.find((c: any) => c.id === 'groupId');
    if (groupColumn) {
      actions.setFilterValue(groupColumn as any, [group.id]);
    }
    setShowGroupModal(false);
  };

  // Handle CSV file selection
  const handleCSVSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCsvFile(file);

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        const parsed = parseCSV(content);
        setCsvData(parsed);
      };
      reader.readAsText(file);
    } catch (err: any) {
      showError(err.message || 'Failed to parse CSV file');
    }
  };

  // Handle batch import
  const handleBatchImport = async () => {
    if (!activeOrganization || !currentUser || csvData.length === 0) return;

    setBatchImporting(true);
    try {
      // Resolve network names to IDs against the full catalogue (loaded on
      // demand + cached). The page no longer keeps all networks in state.
      const catalog = await loadNetworkCatalog(activeOrganization.id);
      const profilesWithIds = csvData.map(row => {
        const network = catalog.find(n => n.name.toLowerCase() === row.network?.toLowerCase());
        const group = groups.find(g => g.name.toLowerCase() === row.group?.toLowerCase());

        return {
          name: row.name,
          description: row.description,
          lpaString: row.lpaString,
          networkId: network?.id || '',
          networkName: network?.name,
          downloadLimit: row.downloadLimit as DownloadLimitType,
          maxDownloads: row.maxDownloads,
          groupId: group?.id,
          groupName: group?.name,
          tags: row.tags,
        };
      });

      const result = await batchImportProfiles(activeOrganization.id, currentUser.uid, profilesWithIds);

      if (result.success > 0) {
        success(`Imported ${result.success} profile(s) successfully`);
      }
      if (result.failed > 0) {
        showError(`Failed to import ${result.failed} profile(s)`);
      }

      setShowBatchUploadModal(false);
      setCsvFile(null);
      setCsvData([]);

      // Reload data
      const [profilesData, statsData] = await Promise.all([
        getProfilesForOrg(activeOrganization.id),
        getInventoryStats(activeOrganization.id),
      ]);
      setProfiles(profilesData);
      setStats(statsData);
    } catch (err: any) {
      showError(err.message || 'Failed to import profiles');
    } finally {
      setBatchImporting(false);
    }
  };

  // Handle update profile group
  // Diff of the detail-modal draft vs the stored profile — exactly the fields
  // Save will write. Empty object = nothing to save.
  const profileDraftChanges = useMemo(() => {
    if (!selectedProfile || !profileDraft) return null;
    const p = selectedProfile;
    const d = profileDraft;
    const changes: Parameters<typeof updateProfile>[2] = {};

    const name = d.name.trim();
    if (name !== (p.name || '').trim()) changes.name = name;
    if (d.networkId !== (p.networkId || '')) {
      changes.networkId = d.networkId;
      changes.networkName = d.networkName;
    }
    if (d.apn.trim() !== (p.apn || '').trim()) changes.apn = d.apn.trim();
    if (d.msisdn.trim() !== (p.msisdn || '').trim()) changes.msisdn = d.msisdn.trim();

    // NaN marks an empty/invalid "limited" cap — kept dirty so Save can
    // surface the validation error instead of silently ignoring the field.
    const nextMax = d.downloadLimit === 'limited'
      ? (typeof d.maxDownloads === 'number' ? Math.floor(d.maxDownloads) : NaN)
      : null;
    const currentMax = p.downloadLimit === 'limited' ? (p.maxDownloads ?? null) : null;
    if (d.downloadLimit !== (p.downloadLimit || 'unlimited') || !Object.is(nextMax, currentMax)) {
      changes.downloadLimit = d.downloadLimit;
      changes.maxDownloads = nextMax;
    }

    if (d.groupId !== (p.groupId || '')) {
      changes.groupId = d.groupId;
      changes.groupName = groups.find(g => g.id === d.groupId)?.name || '';
    }
    if (d.description.trim() !== (p.description || '').trim()) changes.description = d.description.trim();
    if (JSON.stringify(d.tags) !== JSON.stringify(p.tags || [])) changes.tags = d.tags;
    if (d.locked !== (p.locked === true)) {
      changes.locked = d.locked;
      changes.lockedBy = currentUser?.uid ?? null;
    }

    return changes;
  }, [selectedProfile, profileDraft, groups, currentUser]);

  const profileDraftDirty = !!profileDraftChanges && Object.keys(profileDraftChanges).length > 0;

  const closeProfileDetail = () => {
    setShowProfileDetailModal(false);
    setSelectedProfile(null);
    setProfileDraft(null);
    setDetailTagInput('');
  };

  // Save the detail-modal draft in a single update. Download limit stays
  // editable after creation — an activation code's real allowance is often
  // only learned from the carrier later, and a profile wrongly marked
  // single_use is otherwise stuck exhausted forever (raising the cap above
  // `downloadCount` makes it reinstallable again and clears its auto-pin).
  const handleSaveProfileDetail = async () => {
    // Shared mode (super admin, no active org): shared profiles carry
    // organizationId null and firestore.rules let super admins update them;
    // getProfile() exempts shared profiles from the org check.
    if (!selectedProfile || !profileDraft || (!activeOrganization && !sharedMode)) return;

    if (!profileDraft.name.trim()) {
      showError('Name cannot be empty');
      return;
    }
    if (profileDraft.downloadLimit === 'limited') {
      const raw = profileDraft.maxDownloads;
      if (typeof raw !== 'number' || !Number.isFinite(raw) || Math.floor(raw) < 1) {
        showError('Max downloads must be at least 1');
        return;
      }
    }

    const changes = profileDraftChanges;
    if (!changes || Object.keys(changes).length === 0) {
      closeProfileDetail();
      return;
    }

    setSavingProfile(true);
    try {
      await updateProfile(selectedProfile.id, activeOrganization?.id ?? '', changes);

      const group = groups.find(g => g.id === profileDraft.groupId);
      const updatedProfile: ESimProfile = {
        ...selectedProfile,
        name: profileDraft.name.trim(),
        networkId: profileDraft.networkId,
        networkName: profileDraft.networkName || undefined,
        apn: profileDraft.apn.trim() || undefined,
        msisdn: profileDraft.msisdn.trim() || undefined,
        downloadLimit: profileDraft.downloadLimit,
        maxDownloads: profileDraft.downloadLimit === 'limited'
          ? Math.floor(profileDraft.maxDownloads as number)
          : undefined,
        groupId: profileDraft.groupId || undefined,
        groupName: group?.name || undefined,
        description: profileDraft.description.trim() || undefined,
        tags: [...profileDraft.tags],
        locked: profileDraft.locked,
      };
      setProfiles(prev => prev.map(p => p.id === selectedProfile.id ? updatedProfile : p));

      // Reload groups when membership changed so profile counts stay right.
      if (changes.groupId !== undefined && activeOrganization) {
        setGroups(await getGroupsForOrg(activeOrganization.id));
      }

      success('Profile updated');
      closeProfileDetail();
    } catch (err: any) {
      showError(err.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Helper to get selected profiles
  const getSelectedProfiles = () =>
    profiles.filter(p => selectedKeys.has(p.id));

  // Reload all data helper
  const reloadData = async () => {
    if (sharedMode) {
      setProfiles(await getSharedProfiles());
      return;
    }
    if (!activeOrganization) return;
    const [profilesData, groupsData, statsData] = await Promise.all([
      getProfilesForOrg(activeOrganization.id),
      getGroupsForOrg(activeOrganization.id),
      getInventoryStats(activeOrganization.id),
    ]);
    setProfiles(profilesData);
    setGroups(groupsData);
    setStats(statsData);
  };

  // Bulk change group
  const handleBulkChangeGroup = async () => {
    if (!activeOrganization) return;
    const selected = getSelectedProfiles();
    if (selected.length === 0) return;

    setBulkOperating(true);
    try {
      const group = groups.find(g => g.id === bulkGroupId);
      for (const profile of selected) {
        await updateProfile(profile.id, activeOrganization.id, {
          // '' clears the group (the service stores null) — `undefined` would
          // skip the field and silently no-op "No group".
          groupId: bulkGroupId,
          groupName: group?.name || '',
        });
      }
      success(`Updated group for ${selected.length} profile(s)`);
      setShowBulkGroupModal(false);
      setBulkGroupId('');
      setSelectedKeys(new Set());
      await reloadData();
    } catch (err: any) {
      showError(err.message || 'Failed to update groups');
    } finally {
      setBulkOperating(false);
    }
  };

  // Bulk manage tags
  const handleBulkManageTags = async () => {
    if (!activeOrganization) return;
    const selected = getSelectedProfiles();
    if (selected.length === 0) return;

    const newTags = bulkTagsInput.split(',').map(t => t.trim()).filter(Boolean);
    if (newTags.length === 0) {
      showError('Please enter at least one tag');
      return;
    }

    setBulkOperating(true);
    try {
      for (const profile of selected) {
        const existingTags = profile.tags || [];
        const mergedTags = bulkTagMode === 'add'
          ? [...new Set([...existingTags, ...newTags])]
          : newTags;
        await updateProfile(profile.id, activeOrganization.id, { tags: mergedTags });
      }
      success(`Updated tags for ${selected.length} profile(s)`);
      setShowBulkTagsModal(false);
      setBulkTagsInput('');
      setSelectedKeys(new Set());
      await reloadData();
    } catch (err: any) {
      showError(err.message || 'Failed to update tags');
    } finally {
      setBulkOperating(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (!activeOrganization) return;
    const selected = getSelectedProfiles().filter(p => !p.shared);
    if (selected.length === 0) return;

    setBulkOperating(true);
    try {
      for (const profile of selected) {
        await deleteProfile(profile.id, activeOrganization.id);
      }
      success(`Deleted ${selected.length} profile(s)`);
      setShowBulkDeleteModal(false);
      setSelectedKeys(new Set());
      await reloadData();
    } catch (err: any) {
      showError(err.message || 'Failed to delete profiles');
    } finally {
      setBulkOperating(false);
    }
  };

  // Handle delete profile (invoked from the confirmation modal only)
  const handleDeleteProfile = async (profile: ESimProfile) => {
    if (profile.shared) {
      showError('Shared inventory eSIMs cannot be deleted — unshare from the modem\'s eUICC menu first.');
      return;
    }
    if (!activeOrganization) return;

    setDeletingProfile(true);
    try {
      await deleteProfile(profile.id, activeOrganization.id);
      success('Profile deleted successfully');

      // Reload data
      const [profilesData, statsData] = await Promise.all([
        getProfilesForOrg(activeOrganization.id),
        getInventoryStats(activeOrganization.id),
      ]);
      setProfiles(profilesData);
      setStats(statsData);
      setShowDeleteProfileConfirm(false);
      closeProfileDetail();
    } catch (err: any) {
      showError(err.message || 'Failed to delete profile');
    } finally {
      setDeletingProfile(false);
    }
  };

  // Table columns
  // Renders the profile's current eUICC location from the `installedOn` pointer.
  // `compact` is the table cell; the expanded form is used in the detail modal.
  const renderInstalledOn = (profile: ESimProfile, compact = true) => {
    const io = profile.installedOn;
    if (!io?.deviceId || !io?.imei) {
      return (
        <span className="text-muted-foreground/70">
          {compact ? '—' : 'Not currently installed on any modem'}
        </span>
      );
    }
    const locationName = locationNames[io.deviceId] || io.deviceId;
    const model = modemModels[io.imei];
    const enabled = !io.state || io.state === 'enabled';
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />
          <span className={cn('font-medium truncate', compact && 'max-w-[160px]')}>{locationName}</span>
          {io.state && !enabled && (
            <Badge variant="secondary" className="ml-0.5">{io.state}</Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
          <Smartphone className="w-3 h-3 shrink-0" />
          <span className={cn('font-mono truncate', compact && 'max-w-[160px]')}>
            {model ? `${model} · ${io.imei}` : io.imei}
          </span>
        </div>
      </div>
    );
  };

  // One badge answering "can this SIM move to another modem?". The tooltip
  // carries the why, so the table stays scannable.
  const LOCK_BADGE: Record<ESimLockState, { className: string; icon: JSX.Element }> = {
    locked: { className: 'border-accent-yellow/40 bg-accent-yellow/10 text-accent-yellow', icon: <Lock className="w-3 h-3" /> },
    locks_on_install: { className: 'border-border bg-muted text-foreground', icon: <Lock className="w-3 h-3" /> },
    spent: { className: 'border-destructive/40 bg-destructive/10 text-destructive', icon: <XCircle className="w-3 h-3" /> },
    redownloadable: { className: 'border-border bg-transparent text-muted-foreground', icon: <LockOpen className="w-3 h-3" /> },
  };
  const renderLockBadge = (profile: ESimProfile) => {
    const info = getESimLockInfo(profile);
    const style = LOCK_BADGE[info.state];
    return (
      <span
        title={info.detail}
        className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium whitespace-nowrap', style.className)}
      >
        {style.icon}
        {info.label}
      </span>
    );
  };

  const columns: Column<ESimProfile>[] = [
    {
      key: 'name',
      header: 'Profile',
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-blue rounded flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold">{row.name}</div>
            <div className="text-xs text-muted-foreground/70 font-mono truncate max-w-[200px]">
              {row.lpaString ? `${row.lpaString.substring(0, 30)}...` : (row.iccid || 'No activation code')}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'networkName',
      header: 'Network',
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-muted-foreground/70" />
          <span>{row.networkName || 'Unknown'}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_, row) => {
        const status = row.status as ESimProfileStatus;
        const variants: Record<ESimProfileStatus, 'success' | 'warning' | 'error' | 'secondary' | 'default'> = {
          available: 'success',
          in_use: 'default',
          downloaded: 'warning',
          expired: 'error',
          disabled: 'secondary',
        };
        const icons: Record<ESimProfileStatus, React.ReactNode> = {
          available: <CheckCircle className="w-3 h-3" />,
          in_use: <Activity className="w-3 h-3" />,
          downloaded: <Download className="w-3 h-3" />,
          expired: <Clock className="w-3 h-3" />,
          disabled: <XCircle className="w-3 h-3" />,
        };
        const labels: Record<ESimProfileStatus, string> = {
          available: 'available',
          in_use: 'in use',
          downloaded: 'downloaded',
          expired: 'expired',
          disabled: 'disabled',
        };
        return (
          <div className="flex flex-col items-end md:items-start gap-1">
            <Badge variant={variants[status]}>
              {icons[status]}
              <span className="ml-1">{labels[status]}</span>
            </Badge>
            {profileNeedsAttention(row.needsAttention) && <ProfileNeedsAttentionBadge />}
          </div>
        );
      },
    },
    {
      key: 'installedOn',
      header: 'Installed On',
      sortable: false,
      render: (_, row) => renderInstalledOn(row, true),
    },
    {
      key: 'installedCountry',
      header: 'Country',
      sortable: false,
      render: (_, row) => {
        const place = row.installedOn?.deviceId ? installedPlaces[row.installedOn.deviceId] : undefined;
        return place?.country
          ? <span className="whitespace-nowrap">{place.country}</span>
          : <span className="text-muted-foreground/70">—</span>;
      },
    },
    {
      key: 'installedCity',
      header: 'City',
      sortable: false,
      render: (_, row) => {
        const place = row.installedOn?.deviceId ? installedPlaces[row.installedOn.deviceId] : undefined;
        return place?.city
          ? <span className="whitespace-nowrap">{place.city}</span>
          : <span className="text-muted-foreground/70">—</span>;
      },
    },
    {
      key: 'lockState',
      header: 'Lock',
      sortable: false,
      render: (_, row) => renderLockBadge(row),
    },
    {
      key: 'downloadLimit',
      header: 'Limit',
      sortable: true,
      render: (_, row) => {
        const limit = row.downloadLimit;
        if (limit === 'unlimited') {
          return <span className="text-accent-green">Unlimited</span>;
        }
        if (limit === 'single_use') {
          return <span className="text-accent-yellow">Single Use</span>;
        }
        // An installed profile has used at least one download even when the
        // stored counter was never incremented (it only counts successful runs).
        const info = getESimLockInfo(row);
        const used = info.counterLooksStale ? Math.max(row.downloadCount || 0, 1) : row.downloadCount;
        return (
          <span title={info.counterLooksStale ? 'Installed on a modem, so at least one download is used (the stored counter reads lower).' : undefined}>
            {used}/{row.maxDownloads}
          </span>
        );
      },
    },
    {
      key: 'groupName',
      header: 'Group',
      sortable: true,
      render: (_, row) => row.groupName ? (
        <Badge variant="secondary">
          <FolderOpen className="w-3 h-3 mr-1" />
          {row.groupName}
        </Badge>
      ) : (
        <span className="text-muted-foreground/70">-</span>
      ),
    },
    {
      key: 'tags',
      header: 'Tags',
      sortable: false,
      render: (_, row) => {
        const tags = row.tags || [];
        return tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 2).map((tag, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-card">
                <Tag className="w-3 h-3 mr-1" />
                {tag}
              </span>
            ))}
            {tags.length > 2 && (
              <span className="text-xs text-muted-foreground/70">
                +{tags.length - 2}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/70">-</span>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (_, row) => (
        <span className="text-muted-foreground text-sm">
          {formatRelativeTime(row.createdAt)}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-accent-blue mx-auto" />
            <p className="text-muted-foreground">Loading eSIM inventory...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              {sharedMode ? 'Shared eSIM Inventory' : 'eSIM Profile Inventory'}
            </h1>
            <p className="text-body text-muted-foreground">
              {sharedMode
                ? 'Super-admin shared eSIMs (resident SIMs promoted from a modem\'s eUICC menu). Granted orgs can run tests on these. Unshare from the eUICC menu to remove.'
                : 'Manage your eSIM profiles for test execution'}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              icon={<FolderOpen className="w-4 h-4" />}
              onClick={() => setShowGroupModal(true)}
            >
              <span className="hidden sm:inline">Groups</span>
            </Button>
            <Button
              variant="secondary"
              icon={<FileSpreadsheet className="w-4 h-4" />}
              onClick={() => setShowBatchUploadModal(true)}
            >
              <span className="hidden sm:inline">Batch Upload</span>
            </Button>
            <Button
              variant="default"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => setShowUploadModal(true)}
            >
              <span className="hidden sm:inline">Add Profile</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Total Profiles
                </div>
                <div className="text-2xl font-mono font-bold text-foreground">
                  {stats?.totalProfiles || 0}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-blue/10 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-accent-blue" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Available
                </div>
                <div className="text-2xl font-mono font-bold text-accent-green">
                  {stats?.availableProfiles || 0}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-green/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-accent-green" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  In Use
                </div>
                <div className="text-2xl font-mono font-bold text-primary">
                  {stats?.inUseProfiles || 0}
                </div>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-primary" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Downloaded
                </div>
                <div className="text-2xl font-mono font-bold text-accent-yellow">
                  {stats?.downloadedProfiles || 0}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-yellow/10 rounded-lg flex items-center justify-center">
                <Download className="w-6 h-6 text-accent-yellow" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Networks
                </div>
                <div className="text-2xl font-mono font-bold text-foreground">
                  {stats?.totalNetworks || 0}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Network className="w-6 h-6 text-accent" />
              </div>
            </div>
          </Card>
        </div>

        {/* #375: groups whose eSIMs keep moving between modems */}
        {!sharedMode && movingGroups.length > 0 && (
          <Alert className="border-status-warning bg-status-warning-bg">
            <AlertTriangle className="h-4 w-4 text-status-warning" />
            <AlertTitle className="text-status-warning">
              {movingGroups.length === 1
                ? `eSIMs in group "${movingGroups[0].name}" keep moving between modems`
                : `eSIMs in ${movingGroups.length} groups keep moving between modems`}
            </AlertTitle>
            <AlertDescription className="text-foreground">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p>
                  Each move adds time to a run and can use a download.
                  {movingGroups.length > 1 ? ` Groups: ${movingGroups.map((g) => g.name).join(', ')}.` : ''}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start sm:self-auto shrink-0"
                  onClick={() => setShowGroupModal(true)}
                >
                  See what to do
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Profiles Table */}
        <Card padding="lg">
          {/* DataTableFilter - Linear-style column-based filtering */}
          <div className="mb-4 pb-4 border-b border-border">
            <MobileFilterSheet activeCount={filters.length}>
              <DataTableFilter
                columns={filterColumns}
                filters={filters}
                actions={actions}
                strategy={strategy}
              />
            </MobileFilterSheet>
          </div>

          {/* Bulk Action Bar */}
          {selectedKeys.size > 0 && (
            <div className="mb-4 flex items-center gap-3 p-3 bg-accent/50 rounded-lg border border-border">
              <span className="text-sm font-medium text-foreground">
                {selectedKeys.size} selected
              </span>
              <div className="h-4 w-px bg-border" />
              <Button
                variant="outline"
                size="sm"
                icon={<FolderOpen className="w-4 h-4" />}
                onClick={() => setShowBulkGroupModal(true)}
              >
                Change Group
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Tag className="w-4 h-4" />}
                onClick={() => setShowBulkTagsModal(true)}
              >
                Manage Tags
              </Button>
              <Button
                variant="destructive"
                size="sm"
                icon={<Trash2 className="w-4 h-4" />}
                onClick={() => setShowBulkDeleteModal(true)}
              >
                Delete
              </Button>
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedKeys(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          )}

          <ResponsiveTable
            columns={columns}
            data={filteredProfiles}
            keyExtractor={(row) => row.id}
            searchable
            searchPlaceholder="Search profiles by name, network, or tags..."
            paginated
            defaultPageSize={10}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            onRowClick={(row) => {
              setSelectedProfile(row);
              setProfileDraft(buildProfileDraft(row));
              setDetailTagInput('');
              setShowProfileDetailModal(true);
            }}
          />
        </Card>
      </div>

      {/* Add Profile Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          resetForm();
        }}
        title="Add eSIM Profile"
        description="Upload a new eSIM profile via QR code or LPA string"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => {
              setShowUploadModal(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleCreateProfile}
              disabled={formSubmitting}
            >
              {formSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Profile
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Upload mode tabs */}
          <div className="flex gap-2 p-1 bg-card rounded">
            <button
              className={cn(
                'flex-1 py-2 px-4 rounded text-sm font-medium transition-colors',
                uploadMode === 'lpa'
                  ? 'bg-white dark:bg-card text-foreground shadow'
                  : 'text-muted-foreground'
              )}
              onClick={() => setUploadMode('lpa')}
            >
              LPA String
            </button>
            <button
              className={cn(
                'flex-1 py-2 px-4 rounded text-sm font-medium transition-colors',
                uploadMode === 'qr'
                  ? 'bg-white dark:bg-card text-foreground shadow'
                  : 'text-muted-foreground'
              )}
              onClick={() => setUploadMode('qr')}
            >
              QR Code
            </button>
          </div>

          <Input
            label="Profile Name"
            placeholder="My eSIM Profile"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            required
          />

          <Input
            label="Description"
            placeholder="Optional description"
            value={newProfileDescription}
            onChange={(e) => setNewProfileDescription(e.target.value)}
          />

          <Input
            label="Data APN"
            placeholder="internet"
            value={newProfileApn}
            onChange={(e) => setNewProfileApn(e.target.value)}
            helperText="APN used to bring up the modem's data plane for tests on this SIM. Leave blank to inherit the group's default APN, then the network default (falls back to 'internet')."
          />

          {uploadMode === 'qr' ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                QR Code Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleQRUpload}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-medium
                  file:bg-accent-blue file:text-white
                  hover:file:bg-accent-blue/90
                  cursor-pointer"
              />
              {newProfileLpaString && (
                <p className="mt-2 text-sm text-accent-green">
                  QR code parsed successfully
                </p>
              )}
            </div>
          ) : (
            <Input
              label="LPA String"
              placeholder="LPA:1$smdp.example.com$ACTIVATION_CODE"
              value={newProfileLpaString}
              onChange={(e) => setNewProfileLpaString(e.target.value)}
              required
              helperText="Format: LPA:1$<smdp-address>$<activation-code>"
            />
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Network <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            {activeOrganization && (
              <NetworkCombobox
                organizationId={activeOrganization.id}
                userId={currentUser?.uid}
                createScope={networkCreateScope}
                value={newProfileNetworkId}
                selected={newProfileNetwork}
                onChange={(network) => {
                  setNewProfileNetworkId(network.id);
                  setNewProfileNetwork(network);
                }}
              />
            )}
            <p className="text-xs text-muted-foreground">
              Search 2,400+ mobile networks by operator name, country, or MCC/MNC.
              Used for home-operator reporting labels and as an APN fallback — leave
              blank if unknown.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Download Limit</label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={newProfileDownloadLimit}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const next = e.target.value as DownloadLimitType;
                setNewProfileDownloadLimit(next);
                // A single-use code is locked by nature: pre-tick, the user can still untick.
                if (next === 'single_use') setNewProfileLocked(true);
              }}
            >
              <option value="unlimited">Unlimited</option>
              <option value="single_use">Single Use</option>
              <option value="limited">Limited</option>
            </select>
          </div>

          {newProfileDownloadLimit === 'limited' && (
            <Input
              label="Max Downloads"
              type="number"
              min={1}
              value={newProfileMaxDownloads}
              onChange={(e) => setNewProfileMaxDownloads(parseInt(e.target.value, 10))}
            />
          )}

          <label className="flex items-start gap-3 rounded-md border border-input p-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-current"
              checked={newProfileLocked}
              onChange={(e) => setNewProfileLocked(e.target.checked)}
            />
            <span className="space-y-1">
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <Lock className="w-3.5 h-3.5" />
                Lock this SIM to its modem
              </span>
              <span className="block text-xs text-muted-foreground">
                Tick this when the activation code cannot be downloaded again. The first modem the SIM is
                installed on keeps it, and the inventory shows it as locked to that modem and city.
              </span>
            </span>
          </label>

          <div className="space-y-2">
            <label className="text-sm font-medium">Group (Optional)</label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={newProfileGroupId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewProfileGroupId(e.target.value)}
            >
              <option value="">No group</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Tags (Optional)"
            placeholder="test, production, roaming"
            value={newProfileTags}
            onChange={(e) => setNewProfileTags(e.target.value)}
            helperText="Comma-separated list of tags"
          />
        </div>
      </Modal>

      {/* Batch Upload Modal */}
      <Modal
        isOpen={showBatchUploadModal}
        onClose={() => {
          setShowBatchUploadModal(false);
          setCsvFile(null);
          setCsvData([]);
        }}
        title="Batch Upload Profiles"
        description="Upload multiple eSIM profiles from a CSV file"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => downloadCSVTemplate()}>
              Download Template
            </Button>
            <Button variant="ghost" onClick={() => {
              setShowBatchUploadModal(false);
              setCsvFile(null);
              setCsvData([]);
            }}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleBatchImport}
              disabled={batchImporting || csvData.length === 0}
            >
              {batchImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Import {csvData.length} Profile(s)
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVSelect}
              className="block w-full text-sm text-muted-foreground
                file:mr-4 file:py-2 file:px-4
                file:rounded file:border-0
                file:text-sm file:font-medium
                file:bg-accent-blue file:text-white
                hover:file:bg-accent-blue/90
                cursor-pointer"
            />
          </div>

          {csvData.length > 0 && (
            <div className="border border-border dark:border-border-subtle rounded p-4">
              <h4 className="font-medium text-foreground mb-2">
                Preview ({csvData.length} profiles)
              </h4>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {csvData.slice(0, 5).map((row, i) => (
                  <div key={i} className="text-sm p-2 bg-card rounded">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-muted-foreground/70">
                      {row.network} • {row.downloadLimit}
                    </div>
                  </div>
                ))}
                {csvData.length > 5 && (
                  <p className="text-sm text-muted-foreground/70">
                    And {csvData.length - 5} more...
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Group Management Modal */}
      <Modal
        isOpen={showGroupModal}
        onClose={() => {
          setShowGroupModal(false);
          setEditingGroupId(null);
          setMergingGroupId(null);
          setMergeTargetId('');
        }}
        title="Manage Groups"
        description="Create, edit, merge, and delete profile groups"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => {
              setShowGroupModal(false);
              setEditingGroupId(null);
              setMergingGroupId(null);
              setMergeTargetId('');
            }}>
              Close
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {/* Create new group */}
          <div className="border border-border dark:border-border-subtle rounded p-4">
            <h4 className="font-medium text-foreground mb-3">
              Create New Group
            </h4>
            <div className="space-y-3">
              <Input
                label="Group Name"
                placeholder="Production Profiles"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
              <Input
                label="Description"
                placeholder="Optional description"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
              />
              <div className="flex gap-3 items-start">
                <div className="shrink-0">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    value={newGroupColor}
                    onChange={(e) => setNewGroupColor(e.target.value)}
                    className="w-12 h-8 rounded cursor-pointer"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Default APN (Optional)"
                    placeholder="e.g. internet"
                    value={newGroupApn}
                    onChange={(e) => setNewGroupApn(e.target.value)}
                    helperText="Tests on this group's eSIMs use this APN unless the profile sets its own or the test overrides it."
                  />
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateGroup}
                disabled={formSubmitting || !newGroupName}
              >
                {formSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Group
              </Button>
            </div>
          </div>

          {/* Existing groups */}
          <div>
            <h4 className="font-medium text-foreground mb-3">
              Existing Groups ({groups.length})
            </h4>
            {groups.length === 0 ? (
              <p className="text-muted-foreground/70 text-sm">
                No groups created yet
              </p>
            ) : (
              <div className="space-y-2">
                {groups.map((group) => (
                  <div
                    key={group.id}
                    className="p-3 bg-card rounded space-y-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-4 h-4 rounded shrink-0"
                          style={{ backgroundColor: group.color || '#3B82F6' }}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-foreground truncate">
                              {group.name}
                            </span>
                            {(() => {
                              const hint = recentRelocationHint(group.relocationHint);
                              return hint ? <GroupMovingBadge hint={hint} className="shrink-0" /> : null;
                            })()}
                          </div>
                          <div className="text-xs text-muted-foreground/70 flex items-center gap-2 flex-wrap">
                            <span>{group.profileCount} profile{group.profileCount === 1 ? '' : 's'}</span>
                            {group.apn && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted font-mono text-foreground">
                                <Wifi className="w-3 h-3" />
                                APN: {group.apn}
                              </span>
                            )}
                            {group.description && (
                              <span className="truncate">{group.description}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="View this group's profiles in the table"
                          onClick={() => handleViewGroupProfiles(group)}
                          disabled={group.profileCount === 0}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Edit name, color, and default APN"
                          onClick={() => {
                            if (editingGroupId === group.id) {
                              setEditingGroupId(null);
                            } else {
                              startEditGroup(group);
                            }
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Merge into another group"
                          disabled={groups.length < 2}
                          onClick={() => {
                            setEditingGroupId(null);
                            setMergeTargetId('');
                            setMergingGroupId(mergingGroupId === group.id ? null : group.id);
                          }}
                        >
                          <Merge className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title={group.profileCount > 0
                            ? 'Group has profiles — move them or use Merge first'
                            : 'Delete group'}
                          onClick={() => {
                            if (group.profileCount > 0) {
                              showError('Cannot delete group with profiles — move them or use Merge');
                              return;
                            }
                            setPendingGroupDelete(group);
                          }}
                          disabled={group.profileCount > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {renderGroupRelocationAlert(group)}

                    {/* Inline edit form */}
                    {editingGroupId === group.id && (
                      <div className="space-y-3 pt-3 border-t border-border">
                        <Input
                          label="Name"
                          value={editGroupName}
                          onChange={(e) => setEditGroupName(e.target.value)}
                        />
                        <Input
                          label="Description"
                          placeholder="Optional description"
                          value={editGroupDescription}
                          onChange={(e) => setEditGroupDescription(e.target.value)}
                        />
                        <div className="flex gap-3 items-start">
                          <div className="shrink-0">
                            <label className="block text-sm font-medium text-foreground mb-1">
                              Color
                            </label>
                            <input
                              type="color"
                              value={editGroupColor}
                              onChange={(e) => setEditGroupColor(e.target.value)}
                              className="w-12 h-8 rounded cursor-pointer"
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              label="Default APN"
                              placeholder="e.g. internet — leave blank for none"
                              value={editGroupApn}
                              onChange={(e) => setEditGroupApn(e.target.value)}
                              helperText="Used when the profile has no APN of its own and the test doesn't override it."
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleSaveGroupEdit}
                            disabled={groupActionBusy || !editGroupName.trim()}
                          >
                            {groupActionBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Save
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingGroupId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Inline merge picker */}
                    {mergingGroupId === group.id && (
                      <div className="space-y-3 pt-3 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                          Move all {group.profileCount} profile{group.profileCount === 1 ? '' : 's'} into
                          another group, then delete "{group.name}". This cannot be undone.
                        </p>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          value={mergeTargetId}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMergeTargetId(e.target.value)}
                        >
                          <option value="">Select target group…</option>
                          {groups.filter(g => g.id !== group.id).map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleMergeGroup}
                            disabled={groupActionBusy || !mergeTargetId}
                          >
                            {groupActionBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Merge & Delete
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setMergingGroupId(null); setMergeTargetId(''); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Profile Detail Modal */}
      <Modal
        isOpen={showProfileDetailModal}
        onClose={closeProfileDetail}
        title={selectedProfile?.name || 'Profile Details'}
        description="View and edit profile details — changes apply when you save"
        size="lg"
        footer={
          <>
            <Button
              variant="destructive"
              className="sm:mr-auto"
              disabled={selectedProfile?.shared === true}
              title={selectedProfile?.shared ? 'Shared inventory — unshare from the modem\'s eUICC menu first' : undefined}
              onClick={() => setShowDeleteProfileConfirm(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
            <Button variant="ghost" onClick={closeProfileDetail}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={savingProfile || !profileDraftDirty || (!activeOrganization && !sharedMode)}
              title={!activeOrganization && !sharedMode ? 'Select an organization to edit profiles' : undefined}
              onClick={handleSaveProfileDetail}
            >
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </>
        }
      >
        {selectedProfile && profileDraft && (
          <div className="space-y-4">
            {(() => {
              const notice = profileNeedsAttention(selectedProfile.needsAttention);
              return notice ? <ProfileNeedsAttentionAlert notice={notice} /> : null;
            })()}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Name</div>
              <input
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Profile name"
                value={profileDraft.name}
                onChange={(e) => setProfileDraft(d => d && { ...d, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Status</div>
                <Badge variant={
                  selectedProfile.status === 'available' ? 'success' :
                  selectedProfile.status === 'in_use' ? 'default' :
                  selectedProfile.status === 'downloaded' ? 'warning' :
                  selectedProfile.status === 'expired' ? 'error' :
                  'secondary'
                }>
                  {selectedProfile.status === 'in_use' ? 'in use' : selectedProfile.status}
                </Badge>
              </div>
              <div className="col-span-2">
                <div className="text-sm text-muted-foreground mb-1">Installed On</div>
                {renderInstalledOn(selectedProfile, false)}
                {(() => {
                  const place = selectedProfile.installedOn?.deviceId ? installedPlaces[selectedProfile.installedOn.deviceId] : undefined;
                  if (!place?.country && !place?.city) return null;
                  return (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {[place.city, place.country].filter(Boolean).join(', ')}
                    </div>
                  );
                })()}
              </div>
              <div className="col-span-2">
                {(() => {
                  // Preview the lock as it would be after Save.
                  const info = getESimLockInfo({ ...selectedProfile, locked: profileDraft.locked, downloadLimit: profileDraft.downloadLimit, maxDownloads: typeof profileDraft.maxDownloads === 'number' ? profileDraft.maxDownloads : selectedProfile.maxDownloads });
                  const forced = info.source === 'download_budget' || info.source === 'shared_sim';
                  return (
                    <div className="rounded-md border border-input p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          {info.state === 'redownloadable' ? <LockOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                          {info.label}
                        </div>
                        <label className={cn('flex items-center gap-2 text-sm', (forced || sharedMode) && 'opacity-60')}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-current"
                            disabled={forced || sharedMode}
                            checked={forced ? info.state !== 'redownloadable' : profileDraft.locked}
                            onChange={(e) => setProfileDraft(d => d && { ...d, locked: e.target.checked })}
                          />
                          Lock to its modem
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">{info.detail}</p>
                      {forced && info.source === 'download_budget' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Set by the download limit, not by the checkbox. Raise the limit below only if the carrier allows another download.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Network</div>
                {activeOrganization && (
                  <NetworkCombobox
                    organizationId={activeOrganization.id}
                    userId={currentUser?.uid}
                    createScope={networkCreateScope}
                    value={profileDraft.networkId}
                    selected={{
                      id: profileDraft.networkId,
                      name: profileDraft.networkName || 'Unknown',
                    }}
                    onChange={(network) => setProfileDraft(d => d && {
                      ...d,
                      networkId: network.id,
                      networkName: network.name,
                    })}
                  />
                )}
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Data APN</div>
                {(() => {
                  const profileGroup = groups.find(g => g.id === profileDraft.groupId);
                  const groupApn = (profileGroup?.apn || '').trim();
                  return (
                    <>
                      <input
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        placeholder={groupApn ? `${groupApn} (group default)` : 'internet (network/platform default)'}
                        value={profileDraft.apn}
                        onChange={(e) => setProfileDraft(d => d && { ...d, apn: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {profileDraft.apn.trim()
                          ? 'Set on this profile — overrides the group and network defaults.'
                          : groupApn
                            ? `Empty — inherits "${groupApn}" from group "${profileGroup!.name}".`
                            : 'Empty — uses the network default APN, falling back to "internet".'}
                      </p>
                    </>
                  );
                })()}
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Phone number (MSISDN)</div>
                <input
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder="+14155550123 (optional)"
                  value={profileDraft.msisdn}
                  onChange={(e) => setProfileDraft(d => d && { ...d, msisdn: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Used as the caller (A-number) for voice tests when the SIM itself reports no number.
                </p>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Download Limit</div>
                <div className="flex gap-2">
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={sharedMode}
                    value={profileDraft.downloadLimit}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const nextLimit = e.target.value as DownloadLimitType;
                      setProfileDraft(d => d && {
                        ...d,
                        downloadLimit: nextLimit,
                        // Entering `limited` with no usable cap yet — default to
                        // "one more than already used" so the profile can't be
                        // saved straight into "exhausted".
                        maxDownloads: nextLimit === 'limited' && (typeof d.maxDownloads !== 'number' || d.maxDownloads < 1)
                          ? Math.max(selectedProfile.maxDownloads ?? 0, (selectedProfile.downloadCount || 0) + 1)
                          : d.maxDownloads,
                      });
                    }}
                  >
                    <option value="unlimited">Unlimited</option>
                    <option value="single_use">Single Use</option>
                    <option value="limited">Limited</option>
                  </select>
                  {profileDraft.downloadLimit === 'limited' && (
                    <input
                      type="number"
                      min={1}
                      className="flex h-9 w-24 shrink-0 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={sharedMode}
                      value={profileDraft.maxDownloads}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        setProfileDraft(d => d && {
                          ...d,
                          maxDownloads: Number.isFinite(parsed) ? parsed : '',
                        });
                      }}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(() => {
                    const used = selectedProfile.downloadCount || 0;
                    if (profileDraft.downloadLimit === 'unlimited') {
                      return `Downloaded ${used} time(s) — no cap, always reinstallable.`;
                    }
                    const cap = profileDraft.downloadLimit === 'single_use'
                      ? 1
                      : (typeof profileDraft.maxDownloads === 'number' ? profileDraft.maxDownloads : 0);
                    return used >= cap
                      ? `Used ${used} of ${cap} — exhausted. Raise the limit to allow another install.`
                      : `Used ${used} of ${cap} — ${cap - used} install(s) left.`;
                  })()}
                </p>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Group</div>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={profileDraft.groupId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setProfileDraft(d => d && { ...d, groupId: e.target.value })
                  }
                >
                  <option value="">No group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <div className="text-sm text-muted-foreground mb-1">LPA String</div>
              <div className="p-3 bg-card rounded font-mono text-sm break-all">
                {selectedProfile.lpaString}
              </div>
            </div>

            <div>
              <div className="text-sm text-muted-foreground mb-1">Description</div>
              <textarea
                rows={2}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Optional description"
                value={profileDraft.description}
                onChange={(e) => setProfileDraft(d => d && { ...d, description: e.target.value })}
              />
            </div>

            <div>
              <div className="text-sm text-muted-foreground mb-1">Tags</div>
              <div className="flex flex-wrap gap-2 mb-2">
                {profileDraft.tags.map((tag, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    {tag}
                    <button
                      className="ml-1 hover:text-destructive"
                      onClick={() => setProfileDraft(d => d && {
                        ...d,
                        tags: d.tags.filter((_, idx) => idx !== i),
                      })}
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {profileDraft.tags.length === 0 && (
                  <span className="text-muted-foreground/70 text-sm">No tags</span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add tag..."
                  value={detailTagInput}
                  onChange={(e) => setDetailTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && detailTagInput.trim()) {
                      e.preventDefault();
                      const newTag = detailTagInput.trim();
                      setProfileDraft(d => d && (d.tags.includes(newTag) ? d : { ...d, tags: [...d.tags, newTag] }));
                      setDetailTagInput('');
                    }
                  }}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!detailTagInput.trim()}
                  onClick={() => {
                    const newTag = detailTagInput.trim();
                    if (!newTag) return;
                    setProfileDraft(d => d && (d.tags.includes(newTag) ? d : { ...d, tags: [...d.tags, newTag] }));
                    setDetailTagInput('');
                  }}
                >
                  Add
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border dark:border-border-subtle">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Created</div>
                <div className="text-foreground text-sm">
                  {fmt.formatDateTime(selectedProfile.createdAt)}
                </div>
              </div>
              {selectedProfile.lastDownloadedAt && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Last Downloaded</div>
                  <div className="text-foreground text-sm">
                    {fmt.formatDateTime(selectedProfile.lastDownloadedAt)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Profile Confirmation Modal */}
      <Modal
        isOpen={showDeleteProfileConfirm}
        onClose={() => setShowDeleteProfileConfirm(false)}
        title="Delete Profile"
        description="This action cannot be undone."
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteProfileConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deletingProfile}
              onClick={() => selectedProfile && handleDeleteProfile(selectedProfile)}
            >
              {deletingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Profile
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          <span className="text-foreground font-medium">{selectedProfile?.name}</span> will be
          permanently removed from your inventory, along with its activation code (LPA string).
          This does not remove the profile from any eUICC it is already installed on.
        </p>
      </Modal>

      {/* Bulk Change Group Modal */}
      <Modal
        isOpen={showBulkGroupModal}
        onClose={() => { setShowBulkGroupModal(false); setBulkGroupId(''); }}
        title="Change Group"
        description={`Update group for ${selectedKeys.size} selected profile(s)`}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setShowBulkGroupModal(false); setBulkGroupId(''); }}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleBulkChangeGroup}
              disabled={bulkOperating}
            >
              {bulkOperating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <label className="text-sm font-medium">Group</label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={bulkGroupId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBulkGroupId(e.target.value)}
          >
            <option value="">No group</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>
      </Modal>

      {/* Bulk Manage Tags Modal */}
      <Modal
        isOpen={showBulkTagsModal}
        onClose={() => { setShowBulkTagsModal(false); setBulkTagsInput(''); }}
        title="Manage Tags"
        description={`Update tags for ${selectedKeys.size} selected profile(s)`}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setShowBulkTagsModal(false); setBulkTagsInput(''); }}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleBulkManageTags}
              disabled={bulkOperating || !bulkTagsInput.trim()}
            >
              {bulkOperating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mode</label>
            <div className="flex gap-2 p-1 bg-card rounded">
              <button
                className={cn(
                  'flex-1 py-2 px-4 rounded text-sm font-medium transition-colors',
                  bulkTagMode === 'add'
                    ? 'bg-white dark:bg-card text-foreground shadow'
                    : 'text-muted-foreground'
                )}
                onClick={() => setBulkTagMode('add')}
              >
                Add to existing
              </button>
              <button
                className={cn(
                  'flex-1 py-2 px-4 rounded text-sm font-medium transition-colors',
                  bulkTagMode === 'replace'
                    ? 'bg-white dark:bg-card text-foreground shadow'
                    : 'text-muted-foreground'
                )}
                onClick={() => setBulkTagMode('replace')}
              >
                Replace all
              </button>
            </div>
          </div>
          <Input
            label="Tags"
            placeholder="test, production, roaming"
            value={bulkTagsInput}
            onChange={(e) => setBulkTagsInput(e.target.value)}
            helperText="Comma-separated list of tags"
          />
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        title="Delete Profiles"
        description={`Are you sure you want to delete ${selectedKeys.size} profile(s)? This action cannot be undone.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowBulkDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkOperating}
            >
              {bulkOperating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete {selectedKeys.size} Profile(s)
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          The following profiles will be permanently deleted:
        </p>
        <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
          {getSelectedProfiles().map(p => (
            <li key={p.id} className="text-sm text-foreground flex items-center gap-2">
              <Package className="w-3 h-3 text-muted-foreground" />
              {p.name}
            </li>
          ))}
        </ul>
      </Modal>

      <DeleteConfirmDialog
        open={pendingGroupDelete !== null}
        onClose={() => setPendingGroupDelete(null)}
        itemName={pendingGroupDelete?.name}
        onConfirm={async () => {
          const group = pendingGroupDelete;
          if (!group) return;
          try {
            await deleteGroup(group.id, activeOrganization!.id);
            success('Group deleted');
            const groupsData = await getGroupsForOrg(activeOrganization!.id);
            setGroups(groupsData);
          } catch (err: any) {
            showError(err.message);
          }
        }}
      />
    </AppShell>
  );
}

export default ESimInventory;
